/* eslint-disable */
import * as XLSX from 'xlsx';

const REQUIRED_COLUMNS = ['dimensionName', 'positive', 'text_en', 'text_de'];
/**
 * Validates a single question object against the expected schema.
 * Returns an array of error messages (empty if valid).
 */
export function validateQuestion(question) {
  const errors = [];

  if (!question.properties) {
    errors.push('Missing properties object.');
  } else {
    // Check required fields from the mapping.
    if (!question.properties.dimensionName || question.properties.dimensionName.trim() === '') {
      errors.push('dimensionName missing');
    }
    
    // Validate positive column - must be boolean true/false
    if (question.properties.data?.positive === undefined || question.properties.data?.positive === null) {
      errors.push('positive column missing or invalid (must be true or false)');
    } else if (typeof question.properties.data.positive !== 'boolean') {
      errors.push('positive column must be boolean (true or false)');
    }
    
    if (
      !question.properties.content ||
      !question.properties.content.text_questionProgress ||
      !question.properties.content.text_questionProgress.en ||
      question.properties.content.text_questionProgress.en.trim() === ''
    ) {
      errors.push('text_questionProgress.en missing');
    }
    if (
      !question.properties.content ||
      !question.properties.content.text_question ||
      !question.properties.content.text_question.en ||
      question.properties.content.text_question.en.trim() === ''
    ) {
      errors.push('text_question.en missing');
    }
    if (
      !question.properties.content ||
      !question.properties.content.text_questionProgress ||
      !question.properties.content.text_questionProgress.de ||
      question.properties.content.text_questionProgress.de.trim() === ''
    ) {
      errors.push('text_questionProgress.de missing');
    }
    if (
      !question.properties.content ||
      !question.properties.content.text_question ||
      !question.properties.content.text_question.de ||
      question.properties.content.text_question.de.trim() === ''
    ) {
      errors.push('text_question.de missing');
    }
  }

  if (!question.class) {
    errors.push('Missing class.');
  }
  if (!question.type) {
    errors.push('Missing type.');
  }
  if (!Array.isArray(question.viewTimeLogs)) {
    errors.push('viewTimeLogs should be an array.');
  }

  return errors;
}

/**
 * Reads an Excel or CSV file and returns a promise that resolves with an array of question objects.
 * Assumes the first sheet contains the data.
 * The expected column headers in the file are:
 *   dimensionName, dimension, text_questionProgress_en, text_questionProgress_de,
 *   text_question_en, text_question_de, class, type, forceInput
 */
export function parseExcelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Error reading file.'));
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      // Use defval: '' to ensure missing cells become empty strings
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

      if (!jsonData.length) {
        return reject(new Error('The sheet is empty.'));
      }

      const headers = Object.keys(jsonData[0]);
      const missingColumns = REQUIRED_COLUMNS.filter(col => !headers.includes(col));
      if (missingColumns.length > 0) {
        return reject(new Error(`Critical Error: Missing required columns: ${missingColumns.join(', ')}`));
      }
      // Map each row to a question object.
      const questions = jsonData.map((row) => {
        return {
          properties: {
            dimensionName: row['dimensionName'] || '',
            dimension: row['dimension'] || 'x',
            content: {
              text_questionProgress: {
                en: row['text_questionProgress_en'] || '',
                de: row['text_questionProgress_de'] || '',
              },
              text_question: {
                en: row['text_en'] || '',
                de: row['text_de'] || '',
              },
            },
            options: {
              // Assume forceInput is provided as a string "true"/"false" or boolean.
              forceInput: row['forceInput'] === 'true' || row['forceInput'] === true,
            },
            data: {
              // Map the positive column to positive in data
              positive: (() => {
                const positiveValue = row['positive'];
                if (positiveValue === 'true' || positiveValue === true || positiveValue === 1) {
                  return true;
                } else if (positiveValue === 'false' || positiveValue === false || positiveValue === 0) {
                  return false;
                } else {
                  // If invalid value, default to false and log warning
                  console.warn(`Invalid positive value in row: "${positiveValue}". Defaulting to false.`);
                  return false;
                }
              })(),
            },
          },
          class: row['class'] || '',
          type: row['type'] || '',
          viewTimeLogs: [], // Default to an empty array.
        };
      });
      resolve(questions);
    };
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Processes the imported questions by validating each question.
 * Returns an object with two properties:
 *   processed: array of question objects (as parsed from the file)
 *   validationErrors: array of strings containing errors for rows that failed validation.
 */
export function processImportedQuestions(questions) {
  try {
    const processed = [];

    questions.forEach((q, index) => {
      let progressbar = index / questions.length;
      let questionTextProgress = {
        en: 'Statement ' + String(index + 1) + ' of ' + String(questions.length),
        de: 'Statement ' + String(index + 1) + ' von ' + String(questions.length),
      };

      let questionToAdd = { ...q };

      questionToAdd.properties.content.text_questionProgress = questionTextProgress;
      questionToAdd.properties.content.progressbar = progressbar;
      processed.push(questionToAdd);
    });

    return processed;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
